import React from 'react'
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components'
import {
  DesignTokens,
  TailwindColor,
  NeutralColor,
  FontId,
  FontWeight,
  FontSize,
  BorderRadius,
  Shadow,
} from '../types'
import { TAILWIND_COLORS, NEUTRAL_COLORS, COLOR_VALUES } from '../data/tailwindColors'
import {
  FONT_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  FONT_SIZE_OPTIONS,
} from '../data/typography'
import { SelectField } from './SelectField'

interface TokenEditorProps {
  tokens: DesignTokens
  onUpdate: <K extends keyof DesignTokens>(key: K, value: DesignTokens[K]) => void
  hasOverrides: boolean
  onReset: () => void
}

type ColorSelectValue = TailwindColor | 'none'

function ColorPopover({
  label,
  value,
  options,
  onChange,
  allowNone = false,
  showBadge = false,
}: {
  label: string
  value: TailwindColor | null
  options: readonly TailwindColor[]
  onChange: (color: TailwindColor | null) => void
  allowNone?: boolean
  showBadge?: boolean
}) {
  const normalizedValue: ColorSelectValue = value ?? 'none'
  const menuOptions: ColorSelectValue[] = allowNone ? ['none', ...options] : [...options]
  const currentColor =
    normalizedValue === 'none' ? null : COLOR_VALUES[normalizedValue]

  return (
    <DialogTrigger>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600">{label}</label>
          {showBadge && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] rounded-full border"
              style={{
                borderColor: currentColor ?? '#e5e7eb',
                color: currentColor ?? '#9ca3af',
                backgroundColor: currentColor ? `${currentColor}1A` : '#f3f4f6',
              }}
            >
              <span
                className="inline-flex h-2 w-2 rounded-full"
                style={{ backgroundColor: currentColor ?? '#d1d5db' }}
              />
              {normalizedValue === 'none' ? 'No secondary' : normalizedValue}
            </span>
          )}
        </div>
        <Button className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-2">
            {normalizedValue === 'none' ? (
              <span className="text-gray-400 text-xs">None</span>
            ) : (
              <span
                className="inline-flex h-3 w-3 rounded-full border border-gray-200"
                style={{ backgroundColor: COLOR_VALUES[normalizedValue] }}
              />
            )}
            <span className="capitalize">
              {normalizedValue === 'none' ? 'Select color' : normalizedValue}
            </span>
          </div>
          <span className="text-xs text-gray-400">▾</span>
        </Button>
      </div>
      <Popover className="z-50 w-[--trigger-width] rounded-lg border border-gray-200 bg-white shadow-xl">
        <Dialog className="outline-none p-3">
          {({ close }) => (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {label}
                </p>
                {allowNone && (
                  <button
                    onClick={() => {
                      onChange(null)
                      close()
                    }}
                    className="text-[11px] text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-6 gap-2">
                {menuOptions
                  .filter((color) => color !== 'none')
                  .map((color) => (
                    <button
                      key={color}
                      onClick={() => {
                        onChange(color as TailwindColor)
                        close()
                      }}
                      className={`h-9 w-9 rounded-lg border-2 transition cursor-pointer ${
                        normalizedValue === color
                          ? 'border-gray-900 scale-105'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: COLOR_VALUES[color as TailwindColor] }}
                      title={color}
                    />
                  ))}
              </div>
            </div>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}

export function TokenEditor({ tokens, onUpdate, hasOverrides, onReset }: TokenEditorProps) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Customize Tokens
        </h2>
        {hasOverrides && (
          <button
            onClick={onReset}
            className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer"
          >
            Reset to motif defaults
          </button>
        )}
      </div>

      <div className="space-y-3">
        <ColorPopover
          label="Primary Color"
          value={tokens.primaryColor}
          options={TAILWIND_COLORS}
          onChange={(color) => color && onUpdate('primaryColor', color)}
        />

        <ColorPopover
          label="Secondary Color (optional)"
          value={tokens.secondaryColor}
          options={TAILWIND_COLORS}
          onChange={(color) => onUpdate('secondaryColor', color)}
          allowNone
          showBadge
        />

        <ColorPopover
          label="Neutral Color"
          value={tokens.neutralColor}
          options={NEUTRAL_COLORS as unknown as TailwindColor[]}
          onChange={(color) => color && onUpdate('neutralColor', color as NeutralColor)}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Typography
          </h3>
          <span className="text-[11px] text-gray-500">Tailwind scale</span>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <SelectField<FontId>
            label="Display Font"
            value={tokens.displayFont}
            options={FONT_OPTIONS.map((font) => ({
              value: font.id,
              label: font.name,
            }))}
            onChange={(value) => onUpdate('displayFont', value)}
          />

          <div className="grid grid-cols-2 gap-3">
            <SelectField<FontWeight>
              label="Display Weight"
              value={tokens.displayWeight}
              options={FONT_WEIGHT_OPTIONS}
              onChange={(value) => onUpdate('displayWeight', value)}
            />
            <SelectField<FontSize>
              label="Display Size"
              value={tokens.displaySize}
              options={FONT_SIZE_OPTIONS}
              onChange={(value) => onUpdate('displaySize', value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <SelectField<FontId>
            label="Body Font"
            value={tokens.bodyFont}
            options={FONT_OPTIONS.map((font) => ({
              value: font.id,
              label: font.name,
            }))}
            onChange={(value) => onUpdate('bodyFont', value)}
          />

          <div className="grid grid-cols-2 gap-3">
            <SelectField<FontWeight>
              label="Body Weight"
              value={tokens.bodyWeight}
              options={FONT_WEIGHT_OPTIONS}
              onChange={(value) => onUpdate('bodyWeight', value)}
            />
            <SelectField<FontSize>
              label="Body Size"
              value={tokens.bodySize}
              options={FONT_SIZE_OPTIONS}
              onChange={(value) => onUpdate('bodySize', value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <SelectField<FontId>
            label="Mono Font"
            value={tokens.monoFont}
            options={FONT_OPTIONS.map((font) => ({
              value: font.id,
              label: font.name,
            }))}
            onChange={(value) => onUpdate('monoFont', value)}
          />

          <div className="grid grid-cols-2 gap-3">
            <SelectField<FontWeight>
              label="Mono Weight"
              value={tokens.monoWeight}
              options={FONT_WEIGHT_OPTIONS}
              onChange={(value) => onUpdate('monoWeight', value)}
            />
            <SelectField<FontSize>
              label="Mono Size"
              value={tokens.monoSize}
              options={FONT_SIZE_OPTIONS}
              onChange={(value) => onUpdate('monoSize', value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SelectField<BorderRadius>
          label="Border Radius"
          value={tokens.borderRadius}
          options={[
            { value: 'none', label: 'None' },
            { value: 'sm', label: 'Small' },
            { value: 'md', label: 'Medium' },
            { value: 'lg', label: 'Large' },
            { value: 'xl', label: 'Extra Large' },
            { value: 'full', label: 'Full' },
          ]}
          onChange={(value) => onUpdate('borderRadius', value)}
        />

        <SelectField<Shadow>
          label="Shadow"
          value={tokens.shadow}
          options={[
            { value: 'none', label: 'None' },
            { value: 'sm', label: 'Small' },
            { value: 'md', label: 'Medium' },
            { value: 'lg', label: 'Large' },
          ]}
          onChange={(value) => onUpdate('shadow', value)}
        />
      </div>
    </div>
  )
}
